import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { addCategory, updateCategory, deleteCategory, getCategories } from '../../firebase/categories';
import { Category } from '../../types';
import toast from 'react-hot-toast';

const CategoryManager = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newCategory, setNewCategory] = useState({
    name: '',
    parentId: null as string | null,
    isFeaturedTop: false,
    isFeaturedBottom: false,
    order: 0,
  });
  const [isSubcategory, setIsSubcategory] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const categoriesData = await getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setNewCategory({
        name: category.name,
        parentId: category.parentId || null,
        isFeaturedTop: category.isFeaturedTop || false,
        isFeaturedBottom: category.isFeaturedBottom || false,
        order: category.order || 0,
      });
      setIsSubcategory(!!category.parentId);
    } else {
      setEditingCategory(null);
      setNewCategory({
        name: '',
        parentId: null,
        isFeaturedTop: false,
        isFeaturedBottom: false,
        order: 0,
      });
      setIsSubcategory(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setNewCategory({
      name: '',
      parentId: null,
      isFeaturedTop: false,
      isFeaturedBottom: false,
    });
    setIsSubcategory(false);
  };

  const handleSubmit = async () => {
    try {
      if (!newCategory.name.trim()) {
        toast.error('El nombre es obligatorio');
        return;
      }

      if (isSubcategory && !newCategory.parentId) {
        toast.error('Debe seleccionar una categoría principal');
        return;
      }

      if (editingCategory) {
        await updateCategory(editingCategory.id, newCategory);
        toast.success('Categoría actualizada correctamente');
      } else {
        await addCategory(newCategory);
        toast.success('Categoría creada correctamente');
      }
      
      await loadCategories();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error al guardar la categoría');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const hasSubcategories = categories.some(cat => cat.parentId === id);
      if (hasSubcategories) {
        toast.error('No se puede eliminar una categoría que tiene subcategorías');
        return;
      }

      await deleteCategory(id);
      await loadCategories();
      toast.success('Categoría eliminada correctamente');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al eliminar la categoría');
    }
  };

  const mainCategories = categories.filter(cat => !cat.parentId);
  const subCategories = categories.filter(cat => cat.parentId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: '#00ff00' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: 'black',
      border: '1px solid rgba(0, 255, 0, 0.2)',
      borderRadius: 2,
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.1)',
      p: 3 
    }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            bgcolor: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid #ff0000',
            '& .MuiAlert-icon': {
              color: '#ff0000'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#00ff00', textShadow: '0 0 10px rgba(0, 255, 0, 0.3)' }}>
          Gestionar Categorías
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#00ff00',
            color: 'black',
            '&:hover': {
              bgcolor: '#00cc00',
            }
          }}
        >
          Nueva Categoría
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Nombre</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Categoría Principal</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Destacado Superior</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Destacado Inferior</TableCell>
              <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Fecha de Creación</TableCell>
              <TableCell align="right" sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mainCategories.map((category) => (
              <React.Fragment key={category.id}>
                <TableRow sx={{ bgcolor: 'rgba(0, 255, 0, 0.05)' }}>
                  <TableCell sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                    <Typography variant="subtitle1">{category.name}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>-</TableCell>
                  <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                    {category.isFeaturedTop ? 'Sí' : 'No'}
                  </TableCell>
                  <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                    {category.isFeaturedBottom ? 'Sí' : 'No'}
                  </TableCell>
                  <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                    {category.createdAt.toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(category)}
                      sx={{ color: '#00ff00', '&:hover': { color: '#00cc00' } }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(category.id)}
                      sx={{ color: '#ff0000', '&:hover': { color: '#cc0000' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {subCategories
                  .filter(sub => sub.parentId === category.id)
                  .map(subCategory => (
                    <TableRow key={subCategory.id} sx={{ bgcolor: 'rgba(0, 255, 0, 0.02)' }}>
                      <TableCell sx={{ pl: 6, color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                        <Typography variant="body2">{subCategory.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>{category.name}</TableCell>
                      <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                        {subCategory.isFeaturedTop ? 'Sí' : 'No'}
                      </TableCell>
                      <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                        {subCategory.isFeaturedBottom ? 'Sí' : 'No'}
                      </TableCell>
                      <TableCell sx={{ color: '#00ffff', borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                        {subCategory.createdAt.toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(subCategory)}
                          sx={{ color: '#00ff00', '&:hover': { color: '#00cc00' } }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(subCategory.id)}
                          sx={{ color: '#ff0000', '&:hover': { color: '#cc0000' } }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#00ff00', textShadow: '0 0 10px rgba(0, 255, 0, 0.3)' }}>
          Categorías Destacadas
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#00ff00' }}>
              Sección Superior
            </Typography>
            <Paper sx={{ 
              p: 2, 
              minHeight: 100, 
              bgcolor: 'rgba(0, 255, 0, 0.05)',
              border: '1px solid rgba(0, 255, 0, 0.2)',
              boxShadow: '0 0 15px rgba(0, 255, 0, 0.1)'
            }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories
                  .filter(cat => cat.isFeaturedTop)
                  .map(cat => (
                    <Chip
                      key={cat.id}
                      label={cat.name}
                      onDelete={() => {
                        updateCategory(cat.id, { isFeaturedTop: false })
                          .then(() => {
                            loadCategories();
                            toast.success('Categoría actualizada');
                          })
                          .catch(() => toast.error('Error al actualizar la categoría'));
                      }}
                      sx={{
                        m: 0.5,
                        bgcolor: 'rgba(0, 255, 0, 0.1)',
                        border: '1px solid #00ff00',
                        color: '#00ff00',
                        '& .MuiChip-deleteIcon': {
                          color: '#00ff00',
                          '&:hover': { color: '#ff0000' }
                        }
                      }}
                    />
                  ))}
              </Box>
            </Paper>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#00ff00' }}>
              Sección Inferior
            </Typography>
            <Paper sx={{ 
              p: 2, 
              minHeight: 100, 
              bgcolor: 'rgba(0, 255, 0, 0.05)',
              border: '1px solid rgba(0, 255, 0, 0.2)',
              boxShadow: '0 0 15px rgba(0, 255, 0, 0.1)'
            }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories
                  .filter(cat => cat.isFeaturedBottom)
                  .map(cat => (
                    <Chip
                      key={cat.id}
                      label={cat.name}
                      onDelete={() => {
                        updateCategory(cat.id, { isFeaturedBottom: false })
                          .then(() => {
                            loadCategories();
                            toast.success('Categoría actualizada');
                          })
                          .catch(() => toast.error('Error al actualizar la categoría'));
                      }}
                      sx={{
                        m: 0.5,
                        bgcolor: 'rgba(0, 255, 0, 0.1)',
                        border: '1px solid #00ff00',
                        color: '#00ff00',
                        '& .MuiChip-deleteIcon': {
                          color: '#00ff00',
                          '&:hover': { color: '#ff0000' }
                        }
                      }}
                    />
                  ))}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'black',
            border: '1px solid #00ff00',
            boxShadow: '0 0 20px rgba(0, 255, 0, 0.2)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#00ff00', borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}>
          {editingCategory ? `Editar Categoría: ${editingCategory.name}` : 'Nueva Categoría'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            pt: 2,
            '& .MuiTextField-root, & .MuiFormControl-root': {
              '& .MuiOutlinedInput-root': {
                color: '#00ffff',
                '& fieldset': {
                  borderColor: '#00ff00',
                },
                '&:hover fieldset': {
                  borderColor: '#00ff00',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00ff00',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#00ffff',
                '&.Mui-focused': {
                  color: '#00ff00',
                },
              },
            }
          }}>
            <TextField
              fullWidth
              label="Nombre"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            />

            {!editingCategory && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isSubcategory}
                    onChange={(e) => {
                      setIsSubcategory(e.target.checked);
                      setNewCategory({
                        ...newCategory,
                        parentId: null,
                      });
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#00ff00',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 255, 0, 0.1)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#00ff00',
                      },
                    }}
                  />
                }
                label={<Typography sx={{ color: '#00ffff' }}>Es una subcategoría</Typography>}
              />
            )}

            {(isSubcategory || editingCategory?.parentId) && (
              <FormControl fullWidth>
                <InputLabel>Categoría Principal</InputLabel>
                <Select
                  value={newCategory.parentId || ''}
                  label="Categoría Principal"
                  onChange={(e) => setNewCategory({
                    ...newCategory,
                    parentId: e.target.value || null,
                  })}
                >
                  {mainCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              margin="dense"
              label="Orden de aparición"
              type="number"
              fullWidth
              value={newCategory.order}
              onChange={(e) => setNewCategory({ ...newCategory, order: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2 }}
            />

            {(!isSubcategory && !editingCategory?.parentId) && (
              <>
                <Divider sx={{ my: 1, borderColor: 'rgba(0, 255, 0, 0.2)' }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#00ff00' }}>
                  Opciones de Destacado
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newCategory.isFeaturedTop}
                      onChange={(e) => setNewCategory({ ...newCategory, isFeaturedTop: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#00ff00',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 255, 0, 0.1)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#00ff00',
                        },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#00ffff' }}>Destacar en la sección superior</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={newCategory.isFeaturedBottom}
                      onChange={(e) => setNewCategory({ ...newCategory, isFeaturedBottom: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#00ff00',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 255, 0, 0.1)',
                          },
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#00ff00',
                        },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: '#00ffff' }}>Destacar en la sección inferior</Typography>}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)', p: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ 
              color: '#00ffff',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 255, 0.1)',
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              bgcolor: '#00ff00',
              color: 'black',
              '&:hover': {
                bgcolor: '#00cc00',
              }
            }}
          >
            {editingCategory ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManager;